import React, { createContext, useState, ReactNode } from 'react'
import { AppName } from './../constants'
import { TranslationLanguage, useTranslation } from './translation'
import { AvatarInfo } from './../components/avatar'
import { MessagePreviewRequest } from './../pages/modals/preview'
import { MessageInfoRequest } from '../pages/modals/messageinfo/messageInfo'
import { SingleStudentRecipient, SingleTeacherRecipient } from '../pages/home/compose/composeTypes'
import { makeExclusiveRequest, makeRequest, showAxiosErrorAsToast } from '../request'
import { replaceNullWithUndefinedDeep, waitTimeout } from '../utils'
import { Toast, Toasts } from './toast'
import { allUrlParams } from './URLParams'
import { ConferenceLocation } from '../pages/ptc/components/conferenceComponents'
import { Popups } from './popups'
import { SmartLink } from '../components/simple/link'
import { setUserIDInterceptor } from '../hooks/init'

export enum UserType {
  Student = 'student',
  Guardian = 'guardian',
  Teacher = 'teacher',
}

export interface SimpleUser {
  id: number
  name: string
  avatar: AvatarInfo
  type: UserType
  role: string
  location: string
}

export interface Child extends SimpleUser {
  schoolIDs: number[]
  getsAttendanceReports: boolean
}

export interface District {
  id: number
  name: string
  isAdmin: boolean
  phoneEnabled: boolean
  massCallingEnabled: boolean
  allowRecordingPhoneCalls: boolean
  schools: School[]
  showAllRecipients: boolean
  showForwardingOptions: boolean
  abbreviation?: string
  defaultAbbreviation: string
  phoneNumber: string
  allowAllToAddUsers: boolean
  csvImport: boolean
  attendanceEnabled: boolean
  lexikeetForAdminOnly: boolean
  ptcEnabled: boolean
  assessmentsEnabled: boolean
  logoLink?: string
  timeZone: string
}
export interface School {
  id: number
  name: string
  attendanceEnabled: boolean
  attendanceReplies: boolean
  attendanceMessage?: string
  abbreviation?: string
  defaultAbbreviation: string
  displayPhoneNumber: string
  phoneNumber: string
  lexikeet: boolean
  ptcEnabled: boolean
  logoLink?: string
}
interface BaseUser extends SimpleUser {
  email: string
  phoneNumber?: string
  formattedPhoneNumber?: string
  otherAccounts: (SimpleUser & { canSwitchToAccount: boolean })[]
  district: District
  schools: School[]
  faria: boolean
  showPTCConferences: boolean
  hasMessagesFromLastYear: boolean
  massCallingEnabled: boolean
  acceptedTermsOfService: boolean
  showTutorial: boolean
  unreadSystemMessages?: { subject: string; content: string; id: number }[]
  requestUpdatedContacts?: { phoneNumber: string; email?: string }
  preferredName?: string
  systemAdministrator?: boolean
  showAccountSwitcher: boolean
  initialRedirect?: string
  initialToast?: Toast
  hasGoogleOAuth?: boolean
  receiveCalls: boolean
  showAssessments: boolean
  showSurveys: boolean
}

export interface StudentGuardianUser extends BaseUser {
  type: UserType.Student | UserType.Guardian
  pendingEmailChange?: string
  receiveEmails: boolean
  pendingPhoneChange?: string
  receiveTexts: boolean
  receiveTextsAsWhatsapp: boolean
  blockedTextNumbers: string[]
  hasLandline: boolean
  language: string
  spokenLanguage?: string
  allowSelfSISEdits: boolean
  testTeacher?: SingleTeacherRecipient
}

export interface Student extends StudentGuardianUser {
  type: UserType.Student
  oldEnoughForPhone: boolean
}

export interface Guardian extends StudentGuardianUser {
  type: UserType.Guardian
  children: Child[]
  sendAttendanceThroughText: boolean
  sendAttendanceThroughEmail: boolean
}

export interface Teacher extends BaseUser {
  type: UserType.Teacher
  admin: number
  outOfOffice?: string
  defaultOutOfOfficeMessage: string
  allowedToAddFamilies: boolean
  allowedToAddTeachers: boolean
  showMessageInfo: boolean
  showAttendance: boolean
  teacherLexikeet: boolean
  districtAccess: boolean
  externalStaff: boolean
  defaultRecipientType?: 'student' | 'guardian'
  testStudent?: SingleStudentRecipient
  makeCustomRecipient: {
    grades?: { grade: string; schoolIDs: number[] }[]
  }
  PTCAdmin: boolean
  assessmentAdmin: boolean
  visibleEmail: string
  forwardMessagesToEmail: boolean
  forwardSharedMessagesToEmail: boolean
  showAllClasses: boolean
  showGradeGroups: boolean
  requestTeacherType: boolean
  teacherType?: string
  teacherTypeOtherMessage?: string
  newYear?: {
    suggestDeletingClasses?: {
      name: string
      studentsCount: number
      otherTeachersCount: number
      id: number
    }[]
  }
  conferenceLocation: ConferenceLocation
  canEditOwnPhoneNumber: boolean
}

export type User = Student | Teacher | Guardian

export type LoadingValue = {
  key: string
  unmountPage?: boolean
  showLoaderInstantly?: boolean
}
export interface AppState {
  /** The name of the app, usually "ReachMyTeach" */
  appName: AppName
  /** A list of strings representing things that are currently loading.
   * If non-empty, a loading spinner should be shown instead of the normal page.
   * When some certain strings are in this list, the page is unmounted. For example, switching users
   * will unmount the page for safety if components dont react to a new user.
   * */
  loading: LoadingValue[]
  /** The current user, or undefined if not logged in */
  user?: User
  /** The csrf token for the current session. You shouldn't need to add this into requests manually, unless using a <form/> */
  csrf: string
  /** Information about the current language the app is being shown in. This includes the translations for that lanugage. */
  language?: TranslationLanguage
  /** A request to show a message preview modal. When set, the preview modal will launch and show the message corresponding to the request. */
  previewRequest?: MessagePreviewRequest
  /** A request to show a message info modal. When set, the info modal will launch and show the message corresponding to the request. */
  messageInfoRequest?: MessageInfoRequest
}

export interface AppContext {
  /** The current app state, defined above */
  state: AppState
  /** A function to set the app state. */
  setState: React.Dispatch<React.SetStateAction<AppState>>
  /** Start loading something. Given a string, this will add it to the loading list with no other options. You can also
   * provide a LoadingValue object, which allows you to specify a key and whether the page should be unmounted while loading. */
  startLoading: (loading: string | LoadingValue) => void
  /** Stop loading something by its key. */
  stopLoading: (loading: string) => void
  /** Reload the user. If you are switching users, set newUser to true. This will unmount the page while loading. */
  reloadUser: (newUser?: boolean) => Promise<void>
  /** Whether the loading overlay should be shown. This is true if there are any loading values in the loading list. */
  showLoadingOverlay: boolean
  /** Whether the page should be unmounted while loading. This is true if any loading values in the loading list have unmountPage set to true. */
  shouldUnmountPage: boolean
  /** Whether the loader should be shown instantly. This is true if any loading values in the loading list have showLoaderInstantly set to true. */
  showLoaderInstantly: boolean
  /** A function to logout the user. This will redirect to the logout page. */
  logout: () => void
}

export const appContext = createContext<AppContext>({} as AppContext)

export const AppContextProvider: React.FC<{
  children: ReactNode
  appName: AppName
  csrf: string
}> = ({ children, appName, csrf }) => {
  const [state, setState] = useState<AppState>({
    appName,
    csrf,
    loading: [{ key: 'initialSetupLoader', unmountPage: true }],
  })

  // show loader for 350ms on page load, to prevent the loader from flashing on the screen
  React.useEffect(() => {
    const t = setTimeout(() => stopLoading('initialSetupLoader'), 350)
    return () => clearTimeout(t)
  }, [])

  React.useEffect(() => {
    const preventDefault = (e: DragEvent) => {
      e.preventDefault()
    }
    document.addEventListener('dragover', preventDefault)
    document.addEventListener('drop', preventDefault)
    return () => {
      document.removeEventListener('dragover', preventDefault)
      document.removeEventListener('drop', preventDefault)
    }
  }, [])
  const startLoading = (toLoad: string | LoadingValue) => {
    const newLoading = typeof toLoad === 'string' ? { key: toLoad } : toLoad
    setState((oldState) => ({ ...oldState, loading: [...oldState.loading, newLoading] }))
  }
  const stopLoading = (loading: string) => {
    setState((oldState) => ({ ...oldState, loading: oldState.loading.filter((l) => l.key !== loading) }))
  }
  const reloadUser = async (newUser = false) => {
    startLoading({ key: 'getUser', unmountPage: newUser })
    // try skipping the account selector by providing extra info on what this user is trying to do
    const userHints = {
      showingMessageUser: parseInt(allUrlParams().messageUser) || undefined,
      showingMessage: parseInt(allUrlParams().message) || undefined,
    }
    setUserIDInterceptor(undefined) // disable the user ID interceptor, because we may have had the wrong user before
    try {
      const response = await makeExclusiveRequest<User>('/frontend_api/getUser', userHints)
      setUserIDInterceptor(response?.id ?? 'none') // enable the user ID interceptor with the new user ID
      setState((s) => ({ ...s, user: replaceNullWithUndefinedDeep(response) }))
      if (response?.initialToast) Toasts.showToast(response.initialToast)
      console.log('User:', response)
    } catch (e) {
      showAxiosErrorAsToast(e)
      setState((s) => ({ ...s, user: undefined }))
      setUserIDInterceptor(undefined) // disable the user ID interceptor, because we dont really know whats going on here
      console.error('Error getting user:', e)
    } finally {
      stopLoading('getUser')
    }
  }

  const logout = () => {
    startLoading({ key: 'logout', unmountPage: true })
    makeRequest('/frontend_api/logout')
      .then(() => reloadUser(true))
      .catch(showAxiosErrorAsToast)
      .finally(() => stopLoading('logout'))
  }

  const showLoadingOverlay = state.loading.length > 0
  const shouldUnmountPage = state.loading.some((s) => s.unmountPage)
  const showLoaderInstantly = state.loading.some((s) => s.showLoaderInstantly)

  return (
    <appContext.Provider
      value={{
        state,
        setState,
        startLoading,
        stopLoading,
        reloadUser,
        showLoadingOverlay,
        showLoaderInstantly,
        shouldUnmountPage,
        logout,
      }}
    >
      {children}
    </appContext.Provider>
  )
}

export const useSwitchUser = () => {
  const { reloadUser, stopLoading, startLoading } = useAppContext()
  const { changeLanguage } = useTranslation()

  const switchUser = async (userID: number) => {
    startLoading({ key: 'switchUser', showLoaderInstantly: true })
    try {
      const response = await makeRequest<{ error?: string }>('/frontend_api/switchUser', {
        id: userID,
      })
      if (response.error) {
        Popups.confirm({
          title: <b>{response.error}</b>,
          message: <SmartLink to="/logout">Please click here to login again.</SmartLink>,
          yesText: 'Cancel',
        })
      } else {
        // this timeout makes switching accounts look a little smoother
        await waitTimeout(100)
        await reloadUser(true)
        await changeLanguage()
      }
    } catch (e) {
      showAxiosErrorAsToast(e)
    }
    stopLoading('switchUser')
  }
  return switchUser
}

/**
 * A hook to get the app context, including the user, app name, global loading state and more.
 */
export const useAppContext = () => React.useContext(appContext)
